<aside
  class="sidebar"
  [class.sidebar--collapsed]="isCollapsed()"
  role="navigation"
  aria-label="Main navigation"
>
  <!-- Branding Section -->
  <div class="sidebar__branding">
    <div class="branding-container">
      <div class="logo-badge">
        <img src="/Ask_Logo.png" alt="AskLab Logo" class="logo-img" />
      </div>
      <div class="branding-info">
        <h1 class="app-name">AskLab</h1>
        <span class="environment-badge">{{ isAdmin() ? 'Admin Panel' : 'User Portal' }}</span>
      </div>
    </div>
    <button
      class="toggle-btn"
      (click)="toggleSidebar()"
      [attr.aria-label]="isCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
      [attr.aria-expanded]="!isCollapsed()"
      title="Toggle sidebar"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M15 18l-6-6 6-6"></path>
      </svg>
    </button>
  </div>

  <!-- Navigation Menu -->
  <nav class="sidebar__nav" role="menubar">
    @for (item of navItems; track item.id) {
      @if (!item.requiresAdmin || isAdmin()) {
        @if (item.children && item.children.length > 0) {
          <div class="nav-group">
            <button
              class="nav-item nav-item--parent"
              [class.nav-item--active]="isItemActive(item.route)"
              (click)="toggleSubmenu(item.id)"
              [attr.aria-expanded]="expandedMenus[item.id]"
              [attr.aria-label]="item.label"
              role="menuitem"
            >
              <svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <use [attr.href]="'#icon-' + item.id"></use>
              </svg>
              <span class="nav-item__label">{{ item.label }}</span>
              <svg class="nav-item__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            @if (expandedMenus[item.id]) {
              <div class="submenu-container">
                @for (child of item.children; track child.id) {
                  <a
                    [routerLink]="child.route"
                    routerLinkActive="active"
                    [routerLinkActiveOptions]="{ exact: true }"
                    class="submenu-item"
                    [attr.aria-label]="child.label"
                    role="menuitem"
                    (click)="closeSidebarOnMobile()"
                  >
                    <span class="submenu-item__dot"></span>
                    <span class="submenu-item__label">{{ child.label }}</span>
                  </a>
                }
              </div>
            }
          </div>
        } @else {
          <a
            [routerLink]="item.route"
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: false }"
            class="nav-item"
            [attr.aria-label]="item.label"
            role="menuitem"
            (click)="closeSidebarOnMobile()"
          >
            <svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <use [attr.href]="'#icon-' + item.id"></use>
            </svg>
            <span class="nav-item__label">{{ item.label }}</span>
          </a>
        }
      }
    }
  </nav>

  <!-- Footer: User Profile -->
  <div class="sidebar__footer">
    <div class="user-profile">
      <div class="user-avatar">{{ userAvatarLetter() }}</div>
      <div class="user-info">
        <p class="user-name">{{ currentUser()?.name }}</p>
        <p class="user-role">{{ currentUser()?.role?.toUpperCase() }}</p>
      </div>
    </div>
    <button class="logout-btn" (click)="logout()" title="Logout" aria-label="Logout from account">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
      </svg>
    </button>
  </div>

  <!-- SVG Icon Definitions -->
  <svg style="display: none;">
    <defs>
      <symbol id="icon-dashboard" viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </symbol>
      <symbol id="icon-batch-creation" viewBox="0 0 24 24">
        <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
        <path d="M2 17l10 5 10-5"></path>
        <path d="M2 12l10 5 10-5"></path>
      </symbol>
      <symbol id="icon-upload" viewBox="0 0 24 24">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="17 8 12 3 7 8"></polyline>
        <line x1="12" y1="3" x2="12" y2="15"></line>
      </symbol>
      <symbol id="icon-records" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="8" y1="13" x2="16" y2="13"></line>
        <line x1="8" y1="17" x2="16" y2="17"></line>
        <line x1="8" y1="9" x2="10" y2="9"></line>
      </symbol>
      <symbol id="icon-qr-status" viewBox="0 0 24 24">
        <rect x="3" y="3" width="8" height="8" rx="1"></rect>
        <rect x="13" y="3" width="8" height="8" rx="1"></rect>
        <rect x="3" y="13" width="8" height="8" rx="1"></rect>
        <rect x="5" y="5" width="4" height="4" fill="currentColor" stroke="none"></rect>
        <rect x="15" y="5" width="4" height="4" fill="currentColor" stroke="none"></rect>
        <rect x="5" y="15" width="4" height="4" fill="currentColor" stroke="none"></rect>
        <line x1="13" y1="17" x2="13" y2="21"></line>
        <line x1="17" y1="13" x2="21" y2="13"></line>
        <line x1="17" y1="17" x2="21" y2="21"></line>
      </symbol>
      <symbol id="icon-reports" viewBox="0 0 24 24">
        <rect x="3" y="7" width="3" height="10"></rect>
        <rect x="10" y="4" width="3" height="13"></rect>
        <rect x="17" y="10" width="3" height="7"></rect>
        <line x1="3" y1="20" x2="20" y2="20"></line>
      </symbol>
      <symbol id="icon-users" viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </symbol>
    </defs>
  </svg>
</aside>
